import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  dayKey, formatDay, hasPractised, isGameTester, loadPlay, markPractised, nextRun, recordPlay, savePlay, seedDay,
} from "../../games/daily";
import {
  answerNote, answerViewBox, buildPractice, buildQuestions, GAME_ID, GAME_NAME, QUESTIONS, shareText, stadiumFor,
} from "../../games/homeField";
import UsMap from "../../games/UsMap";
import {
  GameMessage, GameOver, GamePage, Helmet, PracticeTag, PRIMARY_BUTTON, Progress, TimerBar, useQuestionClock,
} from "../../games/GameParts";

// Home Field: name the school from the stadium on the map. The rules and the
// questions live in games/homeField.js; this is the screen. See
// games/daily.js for the once-a-day rule and the clock.

function Choice({ team, state, onPick, disabled }) {
  const tone = state === "right" ? "success" : state === "wrong" ? "danger" : null;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", gap: 8, minHeight: 50, padding: "8px 10px", borderRadius: 12, cursor: disabled ? "default" : "pointer",
        border: `2px solid ${tone ? `var(--color-${tone}-border)` : "var(--color-bg-subtle)"}`,
        backgroundColor: tone ? `var(--color-${tone}-bg)` : "var(--color-bg-card)",
        opacity: state === "dim" ? 0.45 : 1, textAlign: "left",
      }}
    >
      <Helmet team={team} size={30} />
      <span style={{ fontSize: 14, fontWeight: 700, color: "var(--color-text-strong)", lineHeight: 1.2 }}>{team?.name}</span>
    </button>
  );
}

function HomeFieldGame() {
  const { teams, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = () => (location.key !== "default" ? navigate(-1) : navigate("/home"));

  const [day] = useState(() => dayKey());
  const practice = useMemo(() => buildPractice(teams), [teams]);

  // A question left on screen when the app was closed counts as out of time.
  // Unlike Higher or Lower, a miss does not end this game.
  const [play, setPlay] = useState(() => {
    const saved = loadPlay(GAME_ID, day);
    if (!saved.done && saved.pending !== null && saved.pending === saved.answers.length) {
      const answers = [...saved.answers, false];
      const resumed = { ...saved, answers, done: answers.length >= QUESTIONS, pending: null };
      savePlay(GAME_ID, day, resumed);
      return resumed;
    }
    return saved;
  });
  const questions = useMemo(() => buildQuestions(teams, seedDay(day, play.run)), [teams, day, play.run]);
  const [practising, setPractising] = useState(() => !hasPractised(GAME_ID) && play.answers.length === 0 && !play.done);
  const [phase, setPhase] = useState(play.done ? "over" : "ask");
  const [picked, setPicked] = useState(null);

  const index = phase === "ask" ? play.answers.length : play.answers.length - 1;
  const q = practising ? practice : questions?.[index];
  const asking = Boolean(q) && phase === "ask";

  useEffect(() => {
    if (!asking || practising) return;
    savePlay(GAME_ID, day, { ...play, pending: play.answers.length });
  }, [asking, practising, day, play]);

  // Report a finished first run of the day. Replays (run > 0) are a tester's
  // own, and repeats are absorbed by the table's unique index.
  useEffect(() => {
    if (practising || !play.done || play.run) return;
    recordPlay(currentUser?.id, GAME_ID, day, play.answers);
  }, [practising, play.done, play.run, play.answers, currentUser, day]);

  const answer = useCallback((slug) => {
    if (phase !== "ask" || !q) return;
    setPicked(slug);
    setPhase("reveal");
    if (practising) return;
    const answers = [...play.answers, slug === q.answer];
    const updated = { ...play, answers, done: answers.length === QUESTIONS, pending: null };
    savePlay(GAME_ID, day, updated);
    setPlay(updated);
  }, [phase, q, practising, play, day]);

  const remaining = useQuestionClock({
    running: asking && !practising,
    questionKey: play.answers.length,
    onExpire: () => answer(null),
  });

  const goOn = () => {
    setPicked(null);
    if (practising) {
      markPractised(GAME_ID);
      setPractising(false);
      setPhase("ask");
    } else {
      setPhase(play.done ? "over" : "ask");
    }
  };

  const playAgain = () => {
    const fresh = nextRun(play);
    savePlay(GAME_ID, day, fresh);
    setPlay(fresh);
    setPicked(null);
    setPhase("ask");
  };

  const dayLabel = formatDay(day);

  if (phase === "over") {
    const score = play.answers.filter(Boolean).length;
    return (
      <GamePage title={GAME_NAME} subtitle={dayLabel}>
        <GameOver
          answers={play.answers}
          total={QUESTIONS}
          text={shareText(play.answers, dayLabel)}
          note={`${score === QUESTIONS ? "A perfect game. " : ""}Back tomorrow for ten new stadiums.`}
          onDone={done}
          onPlayAgain={isGameTester(currentUser?.id) ? playAgain : null}
        />
      </GamePage>
    );
  }

  if (!q) {
    return (
      <GamePage title={GAME_NAME} subtitle={dayLabel}>
        <GameMessage>{Object.keys(teams || {}).length ? "Today's stadiums could not be set up. Try again shortly." : "Loading."}</GameMessage>
      </GamePage>
    );
  }

  const here = stadiumFor(q.answer);
  const revealed = phase === "reveal";
  const note = revealed ? answerNote(q.answer, picked, teams) : null;
  const dots = revealed
    ? q.options.map((slug) => {
      const s = stadiumFor(slug);
      const kind = slug === q.answer ? "right" : slug === picked ? "wrong" : "other";
      return { key: slug, x: s.x, y: s.y, kind, label: teams[slug]?.name };
    })
    : [{ key: "target", x: here.x, y: here.y, kind: "target" }];
  const choiceState = (slug) => {
    if (!revealed) return null;
    if (slug === q.answer) return "right";
    if (slug === picked) return "wrong";
    return "dim";
  };

  return (
    <GamePage title={GAME_NAME} subtitle={dayLabel}>
      {practising ? (
        <>
          <PracticeTag />
          <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--color-text-strong)", margin: "0 0 12px" }}>
            The dot is a stadium. Tap the school that plays there. <b>10 stadiums</b>, 10 seconds each, and every one counts, right or wrong.
          </p>
        </>
      ) : (
        <>
          <Progress answers={play.answers} total={QUESTIONS} current={index} />
          <TimerBar remaining={remaining} stopped={phase !== "ask"} />
        </>
      )}

      <p style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-strong)", margin: "0 0 10px" }}>
        {revealed && !picked ? "Out of time. This one was:" : "Which school plays here?"}
      </p>
      <UsMap
        viewBox={revealed ? answerViewBox(q.options) : undefined}
        dots={dots}
        label={revealed ? `${teams[q.answer]?.name} plays in ${here.city}, ${here.state}` : "A stadium marked on a map of the United States"}
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
        {q.options.map((slug) => (
          <Choice key={slug} team={teams[slug]} state={choiceState(slug)} disabled={revealed} onPick={() => answer(slug)} />
        ))}
      </div>

      {revealed && (
        <>
          <div style={{ marginTop: 12, backgroundColor: "var(--color-bg-subtle)", borderRadius: 12, padding: "10px 12px", fontSize: 13, lineHeight: 1.45, color: "var(--color-text-strong)" }}>
            <b>{note.place}</b>{note.miss ? ` ${note.miss}` : ""}
          </div>
          <button type="button" onClick={goOn} style={{ ...PRIMARY_BUTTON, marginTop: 12 }}>
            {practising ? "Start today's game" : play.done ? "See your score" : "Next"}
          </button>
        </>
      )}
    </GamePage>
  );
}

export default HomeFieldGame;
